import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { DataTable, AlertDialog } from '../Common';
import { stripeAPI } from '../../helpers/api_service';
import {
  formatSubscriptionHistoryItem,
  getStripeApiMessage,
  isCompletedPayment,
  isPendingPayment,
  startSubscriberCheckout,
} from '../../helpers/subscription_payment_helper';
import { formatDate } from '../../helpers/UtilsFunctions';
import './Subscription.css';

// Import icons and assets
import DownloadIcon from '../../assets/Download.svg';
import TrashIcon from '../../assets/Trash.svg';

const getPaymentStatusBadgeStyle = (status, isDarkMode) => {
  if (isPendingPayment(status)) {
    return {
      backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.22)' : '#fff8e1',
      color: isDarkMode ? '#ffc107' : '#b8860b',
      border: `1px solid ${isDarkMode ? 'rgba(255, 193, 7, 0.45)' : '#ffe082'}`,
    };
  }

  if (isCompletedPayment(status)) {
    return {
      backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.22)' : '#e8f5e9',
      color: isDarkMode ? '#81c784' : '#2e7d32',
      border: `1px solid ${isDarkMode ? 'rgba(76, 175, 80, 0.45)' : '#a5d6a7'}`,
    };
  }

  return {
    backgroundColor: isDarkMode ? 'rgba(158, 158, 158, 0.2)' : '#f5f5f5',
    color: isDarkMode ? '#bdbdbd' : '#616161',
    border: `1px solid ${isDarkMode ? 'rgba(158, 158, 158, 0.35)' : '#e0e0e0'}`,
  };
};

const MySubscriptions = ({ subscriptionData }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [billingHistory, setBillingHistory] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [subscriptionDate, setSubscriptionDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userName, setUserName] = useState('User');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [planName, setPlanName] = useState('');
  const [autoRenewal, setAutoRenewal] = useState(true);
  const [planNotes, setPlanNotes] = useState(null);
  const [downloadingRowId, setDownloadingRowId] = useState(null);
  const [payingRowId, setPayingRowId] = useState(null);

  // Initialize theme and get user name on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
    
    // Get user name from localStorage
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        setUserName(user.full_name || user.username || 'User');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, [mode]);

  // Process subscription data when it changes
  useEffect(() => {
    if (subscriptionData) {
      console.log('=== PROCESSING SUBSCRIPTION DATA ===');
      console.log('Subscription data:', subscriptionData);
      
      // Extract current plan information
      if (subscriptionData.activeplan) {
        const activePlan = subscriptionData.activeplan;
        const planDetails = activePlan.subscription_plan;
        
        // Store subscription ID and auto-renewal status for cancellation
        setSubscriptionId(activePlan.id);
        setPlanName(planDetails.title + ' Plan');
        setAutoRenewal(activePlan.auto_renewal !== false); // Default to true if not specified
        
        // Extract notes if available
        if (activePlan.notes && activePlan.notes.trim() !== '') {
          setPlanNotes(activePlan.notes);
        } else {
          setPlanNotes(null);
        }
        
        // Format current plan data
        const formattedCurrentPlan = {
          name: planDetails.title + ' Plan',
          status: activePlan.subscription_status === 'active' ? 'Active' : activePlan.subscription_status,
          description: planDetails.description || 'Your current subscription plan',
          price: `$${activePlan.amount_paid}`,
          period: activePlan.billing_cycle === 'monthly' ? '/Monthly' : '/Yearly',
          isActive: activePlan.subscription_status === 'active'
        };
        
        setCurrentPlan(formattedCurrentPlan);
        
        // Format subscription end date using common utility
        if (activePlan.subscription_end_date) {
          const formattedDate = formatDate(activePlan.subscription_end_date);
          setSubscriptionDate(formattedDate);
        }
        
        console.log('=== FORMATTED CURRENT PLAN ===');
        console.log('Current plan:', formattedCurrentPlan);
        console.log('Subscription date:', subscriptionDate);
      }
      
      // Extract billing history
      if (subscriptionData.subscription_history && Array.isArray(subscriptionData.subscription_history)) {
        const formattedHistory = subscriptionData.subscription_history.map((item, index) =>
          formatSubscriptionHistoryItem(item, index)
        );
        
        setBillingHistory(formattedHistory);
        
        console.log('=== FORMATTED BILLING HISTORY ===');
        console.log('Billing history:', formattedHistory);
      }
    }
  }, [subscriptionData]);

  // Handle export subscription history
  const handleExportList = useCallback(() => {
    if (!billingHistory || billingHistory.length === 0) {
      console.log('No billing history data to export');
      return;
    }

    try {
      // Create CSV headers
      const headers = ['Invoice #', 'Transaction ID', 'Plan Name', 'Date', 'Payment Status', 'Payment Method', 'Amount'];
      
      // Create CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...billingHistory.map(item => [
          `"${item.invoiceNumber}"`,
          `"${item.transactionId}"`,
          `"${item.planName}"`,
          `"${item.date}"`,
          `"${item.paymentStatus}"`,
          `"${item.paymentMethod}"`,
          `"${item.amount}"`
        ].join(','))
      ];
      
      // Join all rows with newlines
      const csvContent = csvRows.join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `subscription_history_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      console.log('Subscription history exported successfully');
    } catch (error) {
      console.error('Error exporting subscription history:', error);
    }
  }, [billingHistory]);

  // Handle cancel plan - show confirmation dialog
  const handleCancelPlan = useCallback(() => {
    if (subscriptionId) {
      setCancelDialogOpen(true);
    } else {
      console.error('No subscription ID available');
    }
  }, [subscriptionId]);

  // Confirm cancel subscription
  const confirmCancelSubscription = useCallback(async () => {
    if (!subscriptionId) {
      console.error('No subscription ID available');
      return;
    }

    try {
      const response = await stripeAPI.cancelSubscription(subscriptionId, { showLoading: false });
      
      if (response.status === 'success') {
        console.log('Subscription cancelled successfully:', response);
        // Refresh the page or update the subscription data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setCancelDialogOpen(false);
    }
  }, [subscriptionId]);

  // Handle upgrade plan
  const handleUpgradePlan = useCallback(() => {
    // Navigate to subscription page with plans parameter
    navigate('/subscription?view=plans');
  }, [navigate]);

  const handleClickToPay = useCallback(async (row) => {
    try {
      setPayingRowId(row.id);
      await startSubscriberCheckout(row, { mode, autoRenewal });
    } catch (error) {
      console.error('Error starting payment:', error);
      alert(getStripeApiMessage(error, 'Failed to start payment. Please try again.'));
      setPayingRowId(null);
    }
  }, [mode, autoRenewal]);

  const handleDownloadInvoice = useCallback(async (row) => {
    const invoiceId = row.invoiceId;

    if (!invoiceId) {
      alert('Invoice ID is not available for this transaction.');
      return;
    }

    try {
      setDownloadingRowId(row.id);
      const response = await stripeAPI.downloadInvoice(invoiceId, {
        loadingMessage: 'Preparing invoice download...',
      });

      if (response.status === 'error') {
        alert(getStripeApiMessage(response));
        return;
      }

      if (response.status === 'success' && response.data?.invoice_pdf_url) {
        const pdfUrl = response.data.invoice_pdf_url;
        const fileName = response.data.invoice_number
          ? `invoice-${response.data.invoice_number}.pdf`
          : `invoice-${String(row.invoiceNumber).replace('#', '')}.pdf`;

        const link = document.createElement('a');
        link.href = pdfUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(getStripeApiMessage(response));
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert(getStripeApiMessage(error));
    } finally {
      setDownloadingRowId(null);
    }
  }, []);

  // Define table columns for billing history with updated structure
  const columns = useMemo(() => [
    {
      accessorKey: 'invoiceNumber',
      header: 'Invoice #',
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.invoiceNumber}
        </span>
      ),
    },
    {
      accessorKey: 'transactionId',
      header: 'Transaction ID',
      size: 150,
      minSize: 130,
      maxSize: 180,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.transactionId}
        </span>
      ),
    },
    {
      accessorKey: 'planName',
      header: 'Plan Name',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.planName}
        </span>
      ),
    },
    {
      accessorKey: 'date',
      header: 'Date',
      size: 180,
      minSize: 160,
      maxSize: 220,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.date}
        </span>
      ),
    },
    {
      accessorKey: 'paymentStatus',
      header: 'Payment Status',
      size: 120,
      minSize: 100,
      maxSize: 150,
      enableSorting: false,
      Cell: ({ row }) => {
        const badgeStyle = getPaymentStatusBadgeStyle(row.original.paymentStatus, mode === 'dark');

        return (
          <span style={{
            ...badgeStyle,
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '600',
            textTransform: 'capitalize',
            whiteSpace: 'nowrap',
          }}>
            {row.original.paymentStatus}
          </span>
        );
      },
    },
    {
      accessorKey: 'paymentMethod',
      header: 'Payment Method',
      size: 150,
      minSize: 130,
      maxSize: 180,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {row.original.paymentMethod}
        </span>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: 100,
      minSize: 80,
      maxSize: 120,
      enableSorting: false,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          fontWeight: '500'
        }}>
          {row.original.amount}
        </span>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 150,
      minSize: 130,
      maxSize: 180,
      enableSorting: false,
      Cell: ({ row }) => {
        const isDownloading = downloadingRowId === row.original.id;
        const hasInvoiceId = Boolean(row.original.invoiceId);
        const pending = isPendingPayment(row.original.paymentStatus);
        const completed = isCompletedPayment(row.original.paymentStatus);

        if (pending) {
          const isPaying = payingRowId === row.original.id;

          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <button
                type="button"
                onClick={() => {
                  if (!isPaying) {
                    handleClickToPay(row.original);
                  }
                }}
                disabled={isPaying}
                style={{
                  background: '#26C3C0',
                  border: '1px solid #26C3C0',
                  color: '#ffffff',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  cursor: isPaying ? 'wait' : 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: isPaying ? 0.7 : 1,
                }}
              >
                {isPaying ? 'Processing...' : 'Click to Pay'}
              </button>
            </div>
          );
        }

        if (completed) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <button
                type="button"
                style={{
                  background: 'transparent',
                  border: `1px solid ${hasInvoiceId && !isDownloading ? '#26C3C0' : mode === 'dark' ? '#444444' : '#cccccc'}`,
                  color: hasInvoiceId && !isDownloading ? '#26C3C0' : mode === 'dark' ? '#666666' : '#999999',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  cursor: hasInvoiceId && !isDownloading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                  opacity: hasInvoiceId && !isDownloading ? 1 : 0.6,
                }}
                onClick={() => {
                  if (hasInvoiceId && !isDownloading) {
                    handleDownloadInvoice(row.original);
                  }
                }}
                title={
                  isDownloading
                    ? 'Downloading invoice...'
                    : hasInvoiceId
                      ? 'Download Invoice'
                      : 'Invoice not available'
                }
                disabled={!hasInvoiceId || isDownloading}
              >
                <img
                  src={DownloadIcon}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: '16px',
                    height: '16px',
                    filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
                    opacity: hasInvoiceId && !isDownloading ? 1 : 0.5,
                  }}
                />
                {isDownloading ? 'Downloading...' : 'Invoice'}
              </button>
            </div>
          );
        }

        return (
          <span style={{
            color: mode === 'dark' ? '#888888' : '#999999',
            fontSize: '13px',
            fontFamily: 'Urbanist, Arial, sans-serif',
          }}>
            —
          </span>
        );
      },
    },
  ], [mode, downloadingRowId, payingRowId, handleDownloadInvoice, handleClickToPay]);

  if (loading) {
    return (
      <div className="subscription-page">
        <Container className="text-center py-5">
          <h1 style={{ 
            color: mode === 'dark' ? '#FFFFFF' : '#333333',
            fontSize: '2rem',
            fontFamily: 'Urbanist, Arial, sans-serif',
            marginBottom: '1rem'
          }}>
            Loading...
          </h1>
        </Container>
      </div>
    );
  }

  return (
    <div className="subscription-page" style={{ padding: '2rem 0' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>

        {/* Subscription Notes Alert */}
        {planNotes && (
          <div style={{ 
            marginBottom: '1.5rem', 
            padding: '0 1rem' 
          }}>
            <div style={{
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(38, 195, 192, 0.15) 0%, rgba(38, 195, 192, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(38, 195, 192, 0.1) 0%, rgba(38, 195, 192, 0.05) 100%)',
              border: `1px solid ${mode === 'dark' ? 'rgba(38, 195, 192, 0.3)' : 'rgba(38, 195, 192, 0.4)'}`,
              borderLeft: `4px solid #26C3C0`,
              borderRadius: '12px',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              boxShadow: mode === 'dark' 
                ? '0 4px 12px rgba(38, 195, 192, 0.1)'
                : '0 2px 8px rgba(38, 195, 192, 0.15)'
            }}>
              {/* Info Icon */}
              <div style={{
                minWidth: '24px',
                height: '24px',
                borderRadius: '50%',
                background: '#26C3C0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '2px'
              }}>
                <span style={{
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  fontFamily: 'Urbanist, Arial, sans-serif'
                }}>
                  i
                </span>
              </div>
              
              {/* Notes Content */}
              <div style={{ flex: 1 }}>
                <h4 style={{
                  color: mode === 'dark' ? '#26C3C0' : '#00807E',
                  fontSize: '16px',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  fontWeight: '600',
                  margin: '0 0 6px 0'
                }}>
                  Important Notice
                </h4>
                <p style={{
                  color: mode === 'dark' ? '#E0E0E0' : '#333333',
                  fontSize: '14px',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  fontWeight: '400',
                  lineHeight: '1.6',
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}>
                  {planNotes}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Plan Card */}
        <div style={{ marginBottom: '2rem', padding: '0 1rem' }}>
          <div style={{
            background: mode === 'dark' ? '#000707' : '#ffffff',
            border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
            borderRadius: '12px',
            padding: '1.5rem',
            position: 'relative'
          }}>
            
            {/* Subscription Due Date Chip - Top Right Inside Card */}
            <div style={{
              position: 'absolute',
              top: '1.5rem',
              right: '1.5rem',
              background: mode === 'dark' ? '#171717' : '#f5f5f5',
              borderRadius: '8px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                color: mode === 'dark' ? '#FFFFFF' : '#333333',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                fontWeight: '400'
              }}>
                Subscription Due Date :
              </span>
              <div style={{
                background: '#003938',
                padding: '4px 12px',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
                fontWeight: '600'
              }}>
                {subscriptionDate}
              </div>
            </div>
             <h2 style={{ 
               color: mode === 'dark' ? '#FFFFFF' : '#333333',
               fontSize: '16px',
               fontFamily: 'Urbanist, Arial, sans-serif',
               fontWeight: '400',
               marginBottom: '24px'
             }}>
              Your Current Plan
            </h2>

            {currentPlan && (
              <div>
                {/* Plan Name and Status */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                   <h3 style={{ 
                     color: mode === 'dark' ? '#FFFFFF' : '#333333',
                     fontSize: '24px',
                     fontFamily: 'Urbanist, Arial, sans-serif',
                     fontWeight: '600',
                     margin: 0
                   }}>
                    {currentPlan.name}
                  </h3>
                   <span style={{
                     background: '#4caf50',
                     color: 'white',
                     padding: '4px 12px',
                     borderRadius: '12px',
                     fontSize: '14px',
                     fontWeight: '500',
                     fontFamily: 'Urbanist, Arial, sans-serif'
                   }}>
                    {currentPlan.status}
                  </span>
                </div>

                {/* Plan Description */}
                 <p style={{ 
                   color: mode === 'dark' ? '#a0a0a0' : '#666666',
                   fontSize: '16px',
                   fontFamily: 'Urbanist, Arial, sans-serif',
                   marginBottom: '24px',
                   lineHeight: '1.4',
                   fontWeight: '400'
                 }}>
                  {currentPlan.description}
                </p>

                {/* Bottom Row - Price and Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: '2rem'
                }}>
                  {/* Plan Price */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px'
                  }}>
                     <span style={{ 
                       color: '#26C3C0',
                       fontSize: '32px',
                       fontFamily: 'Urbanist, Arial, sans-serif',
                       fontWeight: '700'
                     }}>
                      {currentPlan.price}
                    </span>
                     <span style={{ 
                       color: mode === 'dark' ? '#a0a0a0' : '#999999',
                       fontSize: '16px',
                       fontFamily: 'Urbanist, Arial, sans-serif',
                       fontWeight: '400'
                     }}>
                      {currentPlan.period}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}>
                    {/* Only show Cancel Plan button if auto_renewal is true */}
                    {autoRenewal && (
                      <button
                        onClick={handleCancelPlan}
                        style={{
                          background: 'transparent',
                          border: `1px solid #26C3C0`,
                          color: '#26C3C0',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '500',
                          fontFamily: 'Urbanist, Arial, sans-serif',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          minWidth: '120px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#26C3C0';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                          e.target.style.color = '#26C3C0';
                        }}
                      >
                        Cancel Plan
                      </button>
                    )}
                    <button
                      onClick={handleUpgradePlan}
                      style={{
                        background: '#26C3C0',
                        border: '1px solid #26C3C0',
                        color: 'white',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '500',
                        fontFamily: 'Urbanist, Arial, sans-serif',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minWidth: '140px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#1a9a97';
                        e.target.style.borderColor = '#1a9a97';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#26C3C0';
                        e.target.style.borderColor = '#26C3C0';
                      }}
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Subscription History Section */}
        <div style={{ padding: '0 1rem' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{ 
              color: mode === 'dark' ? '#FFFFFF' : '#333333',
              fontSize: '1.5rem',
              fontFamily: 'Urbanist, Arial, sans-serif',
              fontWeight: '600',
              margin: 0
            }}>
              Subscription History
            </h2>
            
            <button
              onClick={handleExportList}
              style={{
                background: 'transparent',
                border: `1px solid #26C3C0`,
                color: '#26C3C0',
                padding: '8px 20px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Urbanist, Arial, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#26C3C0';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#26C3C0';
              }}
            >
              Export List
            </button>
          </div>

          {/* Subscription History Table */}
          <div
            className="my-subscriptions-table-wrapper"
            style={{
              background: mode === 'dark' ? '#141414' : '#ffffff',
              border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <DataTable
              data={billingHistory}
              columns={columns}
              enableRowSelection={false}
              dense={true}
              enableSorting={false}
              enableGlobalFilter={false}
              enableColumnFilters={false}
              enablePagination={true}
              enableColumnActions={false}
              enableColumnDragging={false}
              enableColumnResizing={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableTopToolbar={false}
              enableBottomToolbar={true}
              pageSize={10}
              pageSizeOptions={[5, 10, 25, 50]}
              showFirstButton={true}
              showLastButton={true}
              emptyMessage="No subscription history found"
              hover={true}
              striped={false}
              border={false}
              shadow={false}
              borderRadius="0"
              showHorizontalScroll={true}
              customContainerProps={{
                sx: {
                  backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                  '& .MuiTable-root': {
                    borderCollapse: 'collapse',
                    borderSpacing: 0,
                    backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                  },
                  '& .MuiTableBody-root': {
                    backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                  },
                },
              }}
              customBodyProps={{
                sx: {
                  '& .MuiTableRow-root': {
                    margin: '0 !important',
                    marginBottom: '0 !important',
                  },
                },
              }}
              customTableProps={{
                sx: {
                  backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                  borderCollapse: 'collapse',
                  borderSpacing: 0,
                  '& .MuiTableBody-root': {
                    backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                  },
                  '& .MuiTableRow-root': {
                    height: 'auto !important',
                    minHeight: 'unset !important',
                    margin: 0,
                    backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                    '&:nth-of-type(even)': {
                      backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                    },
                    '&:hover': {
                      backgroundColor: mode === 'dark' ? '#1f1f1f' : '#f5f5f5',
                    },
                  },
                  '& .MuiTableCell-head': {
                    padding: '10px 12px !important',
                    backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f8f9fa',
                    borderBottom: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                  },
                  '& .MuiTableCell-body': {
                    padding: '8px 12px !important',
                    lineHeight: 1.35,
                    backgroundColor: mode === 'dark' ? '#141414' : '#ffffff',
                    borderBottom: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                    borderTop: 'none',
                  },
                  '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                    borderBottom: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
                  },
                },
              }}
            />
          </div>
        </div>
      </Container>

      {/* Cancel Subscription Confirmation Dialog */}
      <AlertDialog
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={confirmCancelSubscription}
        type="delete"
        title="Cancel Subscription"
        message={`Are you sure you want to cancel your ${planName}? Your subscription will remain active until the end of the current billing period.`}
        confirmText="Cancel Subscription"
        cancelText="Keep Subscription"
        icon={TrashIcon}
      />
    </div>
  );
};

export default MySubscriptions;
